import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getDatabase, ref, get, onValue, set, update, push } from 'firebase/database';
import { useAuth } from './AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { secureLogger } from '../utils/loggerUtils';

/**
 * Subscription Verification System
 * 
 * This implementation uses an adaptive verification schedule to optimize resource usage while
 * maintaining accurate subscription status checks. Verification frequencies are adjusted based
 * on the subscription's status and proximity to expiration.
 * 
 * Verification Schedule:
 * - Active subscriptions (not expiring soon): Once per day (3600000ms)
 * - Approaching expiration (<7 days): Progressively more frequent checks
 * - Canceled subscriptions: Every 12 hours
 * - Already expired: Once per day
 * 
 * The system stores the next verification time in the database to coordinate checks
 * across multiple devices and prevent redundant verifications.
 */

// Updated constants to include subscription nearing end detection
const VERIFICATION_COOLDOWN_MS = 86400000; // Base cooldown: 24 hours (once per day)
const MIN_VERIFICATION_COOLDOWN_MS = 60000; // Minimum: 1 minute
const APPROACHING_EXPIRATION_THRESHOLD_MS = 7 * 24 * 3600000; // 7 days
const NEARING_END_THRESHOLD_MS = 7 * 24 * 3600000; // 7 days warning for subscription ending soon
const VERIFICATION_TIMEOUT_MS = 15000; // 15 seconds timeout for verification requests

// To force revalidation periodically for canceled subscriptions
const FORCE_VERIFICATION_INTERVAL_MS = 86400000; // 24 hours between forced verifications

// Minimum time between verification attempts in the UI to prevent excessive checks
const MIN_UI_VERIFICATION_INTERVAL_MS = 30000; // 30 seconds between UI-triggered verification attempts

// Flag to prevent verification during database updates
const UPDATE_SOURCE = {
  LOCAL_CHECK: 'local_check', 
  DB_LISTENER: 'db_listener'
};

// Constants for localStorage keys
const SUBSCRIPTION_DETAILS_KEY = 'subscriptionDetails';
const LAST_VERIFICATION_KEY = 'lastSubscriptionVerification';
const INITIAL_VERIFICATION_KEY = 'initialVerificationDone';

// Helper function to check if verification was done today
const wasVerifiedToday = (): boolean => {
  const lastVerificationTime = localStorage.getItem(LAST_VERIFICATION_KEY);
  if (!lastVerificationTime) return false;
  
  const lastTime = parseInt(lastVerificationTime, 10);
  const now = Date.now();
  
  // Check if last verification was today (same calendar day)
  const lastDate = new Date(lastTime);
  const nowDate = new Date(now);
  
  return lastDate.getDate() === nowDate.getDate() && 
         lastDate.getMonth() === nowDate.getMonth() && 
         lastDate.getFullYear() === nowDate.getFullYear();
};

// Add this debug function at the top of the file after imports
function calculateDataSize(data: any): string {
  const jsonString = JSON.stringify(data);
  const bytes = new TextEncoder().encode(jsonString).length;
  const kilobytes = bytes / 1024;
  return kilobytes > 1024 ? `${(kilobytes / 1024).toFixed(2)} MB` : `${kilobytes.toFixed(2)} KB`;
}

/**
 * Calculates the appropriate next verification time based on subscription status
 * Subscriptions closer to expiration are verified more frequently
 * 
 * @param subscription The subscription details to analyze
 * @returns Timestamp (ms) when the next verification should occur
 */
const getNextVerificationTime = (subscription: SubscriptionDetails | null) => {
  const now = Date.now();
  
  // If no subscription or missing data, use default cooldown
  if (!subscription || !subscription.periodEnd) {
    return now + VERIFICATION_COOLDOWN_MS;
  }
  
  // Ensure periodEnd is properly converted to a timestamp
  let periodEndTime: number;
  try {
    if (subscription.periodEnd instanceof Date) {
      periodEndTime = subscription.periodEnd.getTime();
    } else if (typeof subscription.periodEnd === 'number') {
      periodEndTime = subscription.periodEnd;
    } else if (typeof subscription.periodEnd === 'string') {
      periodEndTime = new Date(subscription.periodEnd).getTime();
    } else {
      secureLogger.error('Invalid periodEnd format:', subscription.periodEnd);
      return now + VERIFICATION_COOLDOWN_MS;
    }
  } catch (error) {
    secureLogger.error('Error processing periodEnd:', error, subscription.periodEnd);
    return now + VERIFICATION_COOLDOWN_MS;
  }
  
  const timeUntilExpiration = periodEndTime - now;
  
  // Logic for determining next check based on subscription status
  if (subscription.status === 'expired') {
    // For explicitly expired subscriptions, use a very long cooldown (48 hours)
    // This helps prevent verification loops for expired subscriptions
    return now + (VERIFICATION_COOLDOWN_MS * 2);
  } else if (timeUntilExpiration <= 0) {
    // Already expired but not marked as such - check infrequently
    return now + VERIFICATION_COOLDOWN_MS;
  } else if (timeUntilExpiration < APPROACHING_EXPIRATION_THRESHOLD_MS) {
    // Approaching expiration - check more frequently
    // Scale check frequency based on how close to expiration
    const scalingFactor = Math.max(0.1, timeUntilExpiration / APPROACHING_EXPIRATION_THRESHOLD_MS);
    return now + Math.max(MIN_VERIFICATION_COOLDOWN_MS, Math.floor(VERIFICATION_COOLDOWN_MS * scalingFactor));
  } else if (subscription.cancelAtPeriodEnd || subscription.status === 'canceled' || subscription.status === 'inactive') {
    // Canceled/inactive but not expired - check every 12 hours
    return now + (VERIFICATION_COOLDOWN_MS / 2);
  } else {
    // Active and not close to expiration - check daily
    return now + VERIFICATION_COOLDOWN_MS;
  }
};

/**
 * Adds random jitter to verification time to prevent synchronized API calls
 * from multiple users at the same time
 * 
 * @param time Base time value
 * @returns Time with added random jitter
 */
const addJitter = (time: number) => {
  // Add random jitter of up to 10% of the interval
  const jitterFactor = 0.1; 
  const maxJitter = time * jitterFactor;
  return time + Math.floor(Math.random() * maxJitter);
};

interface SubscriptionContextType {
  isPremium: boolean;
  isLoading: boolean;
  error: string | null;
  subscriptionDetails: SubscriptionDetails | null;
  upgradeToPremuim: () => Promise<void>;
  checkSubscription: (forceCheck?: boolean) => Promise<boolean>;
  cancelSubscription: () => Promise<boolean>;
  getSubscriptionStatusDisplay: () => React.ReactNode;
  verifyBeforeSavingTest: () => Promise<boolean>;
}

// Expanded interface to include more subscription details
interface SubscriptionDetails {
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trial' | 'expired' | 'paused' | 'refunded' | 'inactive';
  periodEnd: Date | null;
  periodStart: Date | null;
  cancelAtPeriodEnd: boolean;
  platform: 'web' | 'ios' | 'android' | 'macos' | 'unknown';
  planId?: string;
  lastVerified?: Date;
  nextVerificationTime?: number;  // Added field for next verification time
  // Platform-specific receipt data
  appleReceipt?: AppleReceiptData;
  googlePlayReceipt?: GooglePlayReceiptData;
  webReceipt?: WebReceiptData;
  // Cross-platform verification status
  lastVerificationResult?: 'success' | 'failed' | 'pending' | 'inactive';
  verificationMessages?: string[];
  // User-friendly status display
  displayStatus?: 'Active' | 'Canceled (Active Until Expiration)' | 'Inactive (Active Until Expiration)' | 
                 'Expired' | 'Expired (Canceled)' | 'Expired (Inactive)' | 'Trial' | 'On Hold' | 
                 'Active (Renews Soon)' | 'Expiring Soon' | 'Active (Auto-Renew)';
  // Flag to indicate a subscription that's been canceled but is still active until period end
  isCanceledButActive?: boolean;
  // Flag to indicate if subscription is nearing end
  isNearingEnd?: boolean;
  canCreateTest: boolean;
  // Auto-renewal status
  autoRenewing?: boolean;
  // Next renewal date for auto-renewing subscriptions
  nextRenewalDate?: Date | null;
}

// Apple App Store receipt data
interface AppleReceiptData {
  originalTransactionId: string;
  latestReceiptData: string; // Base64 encoded receipt
  environment: 'Production' | 'Sandbox';
  expiresDate: number; // Timestamp in milliseconds
  productId: string;
  autoRenewStatus: boolean;
  isInBillingRetryPeriod?: boolean;
  offerCodeRefName?: string;
}

// Google Play receipt data
interface GooglePlayReceiptData {
  packageName: string;
  productId: string;
  purchaseToken: string;
  purchaseTime: number; // Timestamp in milliseconds
  orderId: string;
  autoRenewing: boolean;
  acknowledgementState: number;
  purchaseState: number;
  developerPayload?: string;
  obfuscatedAccountId?: string;
}

// Web receipt data (Stripe, etc.)
interface WebReceiptData {
  customerId: string;
  subscriptionId: string;
  paymentIntentId?: string;
  invoiceId?: string;
  paymentMethod?: string;
  paymentMethodType?: string;
}

interface FreeUserLimits {
  MAX_TESTS: number;
  MAX_STORAGE_MB: number;
  MAX_DEVICES: number;
}

const FREE_USER_LIMITS: FreeUserLimits = {
  MAX_TESTS: 10,
  MAX_STORAGE_MB: 100,
  MAX_DEVICES: 2
};

// DEBUG FLAGS - Set to true to disable all verification methods
const DEBUG_DISABLE_ALL_VERIFICATION = false; // Set to false to enable verification
const DEBUG_DISABLE_EXPIRED_VERIFICATION = false; // Set to true to disable verification for expired subscriptions
const DEBUG_ALLOW_PREMIUM_REGARDLESS = false; // Set to false to respect actual premium status

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/**
 * Get a user-friendly display status for the subscription
 */
const getDisplayStatus = (
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trial' | 'expired' | 'paused' | 'refunded' | 'inactive',
  isExpired: boolean,
  isNearingEnd: boolean
): string => {
  if (status === 'expired' || isExpired) {
    return 'Expired';
  }
  
  if (status === 'canceled') {
    return isExpired ? 'Expired (Canceled)' : 'Canceled (Active Until Expiration)';
  }
  
  if (status === 'inactive') {
    return isExpired ? 'Expired (Inactive)' : 'Inactive (Active Until Expiration)';
  }
  
  if (status === 'trial') {
    return 'Trial';
  }
  
  if (status === 'paused') {
    return 'On Hold';
  }
  
  if (status === 'refunded') {
    return 'Refunded';
  }
  
  if (isNearingEnd) {
    return 'Expiring Soon';
  }
  
  return 'Active';
};

/**
 * Determine if a subscription is set to auto-renew
 */
const getAutoRenewingStatus = (subData: any): boolean => {
  // For Android, check the Google Play receipt
  if (subData.platform === 'android' && subData.googlePlayReceipt) {
    return subData.googlePlayReceipt.autoRenewing === true;
  }
  
  // For iOS, check the Apple receipt
  if ((subData.platform === 'ios' || subData.platform === 'macos') && subData.appleReceipt) {
    return subData.appleReceipt.autoRenewStatus === true;
  }
  
  // For web or unknown platforms, check cancelAtPeriodEnd
  return subData.cancelAtPeriodEnd !== true;
};

export const SubscriptionProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const { currentUser } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [storageUsage, setStorageUsage] = useState<number>(0);
  const [activeDevices, setActiveDevices] = useState<number>(0);
  const [lastForceVerification, setLastForceVerification] = useState<number>(0);
  const currentUpdateSource = useRef<string | null>(null);
  const lastVerifiedRef = useRef<number>(0);
  const lastUIVerificationRef = useRef<number>(0); // Track UI-triggered verification attempts
  const processedRef = useRef(false); // Track if subscription data has been processed
  const [initialVerificationDone, setInitialVerificationDone] = useState<boolean>(
    localStorage.getItem(INITIAL_VERIFICATION_KEY) === 'true'
  );
  const isVerifyingRef = useRef<boolean>(false); // Track if verification is in progress
  
  // Initialize lastVerifiedRef to current time when component mounts
  useEffect(() => {
    lastVerifiedRef.current = Date.now();
    lastUIVerificationRef.current = Date.now() - MIN_UI_VERIFICATION_INTERVAL_MS; // Allow first verification
  }, []);

  /**
   * Handle checking Android-specific subscription cancellation status
   */
  const checkAndroidCancellationStatus = useCallback(async (subData: any) => {
    if (!currentUser) return;
    
    if (subData.platform !== 'android' || 
        !subData.googlePlayReceipt) {
      // Not an Android subscription
      return;
    }

    try {
      secureLogger.log('Checking Android subscription cancellation status');
      
      const db = getDatabase();
      const subscriptionRef = ref(db, `users/${currentUser.uid}/subscription`);
      
      // Detect inconsistent states between autoRenewing and cancelAtPeriodEnd
      
      // Case 1: autoRenewing is false but cancelAtPeriodEnd is not true
      if (subData.googlePlayReceipt.autoRenewing === false && 
          subData.cancelAtPeriodEnd !== true) {
        
        secureLogger.log('Found Android subscription with autoRenewing=false but cancelAtPeriodEnd!=true - fixing inconsistency');
        
        // Update both cancelAtPeriodEnd and isCanceledButActive flags
        await update(subscriptionRef, {
          cancelAtPeriodEnd: true,
          isCanceledButActive: true,
          verificationMessages: [
            ...(subData.verificationMessages || []),
            `Fixed inconsistent cancellation state on ${new Date().toISOString()}`
          ]
        });
      }
      
      // Case 2: Check expiration for canceled subscriptions
      if (subData.cancelAtPeriodEnd === true && 
          subData.periodEnd && 
          Date.now() > subData.periodEnd) {
        
        secureLogger.log('Found expired but not properly marked canceled subscription - updating status');
        
        // Update status to explicitly canceled
        await update(subscriptionRef, {
          status: 'canceled',
          isCanceledButActive: false,
          verificationMessages: [
            ...(subData.verificationMessages || []),
            `Marked subscription as fully expired on ${new Date().toISOString()}`
          ]
        });
      }
    } catch (error) {
      secureLogger.error('Error updating Android cancellation status:', error);
    }
  }, [currentUser]);

  /**
   * Check specifically for Google Play cancellations
   * This function directly queries Google Play purchase status 
   * to detect when a user has canceled through Google Play Store
   */
  const checkGooglePlayCancellationStatus = useCallback(async (options: { force?: boolean } = {}): Promise<void> => {
    if (!currentUser || !subscriptionDetails) return;
    
    // Only check for Android subscriptions
    if (subscriptionDetails.platform !== 'android' || 
        !subscriptionDetails.googlePlayReceipt ||
        !subscriptionDetails.googlePlayReceipt.purchaseToken) {
      return;
    }
    
    try {
      secureLogger.log('Performing direct Google Play subscription status check');
      
      // If force=true is passed, we'll simulate a cancellation for testing
      if (options.force === true) {
        secureLogger.log('Forced Google Play cancellation check - simulating cancellation');
        
        // Simulate a Google Play cancellation by updating the database directly
        const db = getDatabase();
        const subscriptionRef = ref(db, `users/${currentUser.uid}/subscription`);
        
        await update(subscriptionRef, {
          cancelAtPeriodEnd: true,
          status: 'canceled',
          isCanceledButActive: true,
          verificationMessages: [
            ...(subscriptionDetails.verificationMessages || []),
            `Simulated Google Play cancellation on ${new Date().toISOString()}`
          ]
        });
        
        // Also update the Google Play receipt
        const receiptRef = ref(db, `users/${currentUser.uid}/subscription/googlePlayReceipt`);
        await update(receiptRef, {
          autoRenewing: false
        });
        
        secureLogger.log('Updated database with simulated cancellation');
        return;
      }
      
      // Call function to check Google Play status
      const functions = getFunctions();
      const checkGooglePlayStatusFunc = httpsCallable(functions, 'checkGooglePlaySubscriptionStatus');
      const result = await checkGooglePlayStatusFunc({
        purchaseToken: subscriptionDetails.googlePlayReceipt.purchaseToken
      });
      
      const data = result.data as any;
      
      // Check for cancellation status
      if (data) {
        const db = getDatabase();
        const subscriptionRef = ref(db, `users/${currentUser.uid}/subscription`);
        const updates: Record<string, any> = {};
        let needsUpdate = false;

        // Check if auto-renewal status has changed
        if (data.autoRenewing === false && 
            subscriptionDetails.googlePlayReceipt.autoRenewing !== false) {
          secureLogger.log('Google Play subscription auto-renewal was turned off');
          updates.cancelAtPeriodEnd = true;
          updates.isCanceledButActive = true;
          updates.status = 'canceled';
          needsUpdate = true;
        }

        // Check if subscription was refunded or canceled
        if (data.purchaseState === 1) { // 1 = Canceled
          secureLogger.log('Google Play subscription was canceled');
          updates.status = 'canceled';
          updates.isCanceledButActive = false;
          needsUpdate = true;
        }

        // Check if subscription expired
        if (data.expiryTime && data.expiryTime < Date.now()) {
          secureLogger.log('Google Play subscription has expired');
          updates.status = 'canceled';
          updates.isCanceledButActive = false;
          needsUpdate = true;
        }

        if (needsUpdate) {
          // Add verification message
          updates.verificationMessages = [
            ...(subscriptionDetails.verificationMessages || []),
            `Google Play status updated on ${new Date().toISOString()}: ${JSON.stringify(data)}`
          ];

          // Update subscription status
          await update(subscriptionRef, updates);

          // If subscription was canceled, also update the receipt
          if (data.autoRenewing === false) {
            const receiptRef = ref(db, `users/${currentUser.uid}/subscription/googlePlayReceipt`);
            await update(receiptRef, {
              autoRenewing: false,
              purchaseState: data.purchaseState || 0
            });
          }

          // Update local state
          setSubscriptionDetails(prev => {
            if (!prev) return null;
            return {
              ...prev,
              ...updates,
              googlePlayReceipt: prev.googlePlayReceipt ? {
                ...prev.googlePlayReceipt,
                autoRenewing: data.autoRenewing,
                purchaseState: data.purchaseState
              } : undefined
            };
          });
        }
      }
    } catch (error) {
      secureLogger.error('Error checking Google Play cancellation status:', error);
    }
  }, [currentUser, subscriptionDetails]);

  // Add a function to check Android subscription status periodically
  useEffect(() => {
    if (!currentUser || !subscriptionDetails) return;

    // Only set up periodic checks for Android subscriptions
    if (subscriptionDetails.platform !== 'android') return;

    // Check immediately if subscription is nearing end
    if (subscriptionDetails.isNearingEnd) {
      checkGooglePlayCancellationStatus();
    }

    // Set up periodic checks for Android subscriptions
    const intervalId = setInterval(() => {
      if (subscriptionDetails.isNearingEnd) {
        checkGooglePlayCancellationStatus();
      }
    }, 3600000); // Check every hour for subscriptions nearing end

    return () => clearInterval(intervalId);
  }, [currentUser, subscriptionDetails, checkGooglePlayCancellationStatus]);

  /**
   * Check specifically for subscription renewal status when approaching end date
   * This is particularly important for Android subscriptions where auto-renewal
   * status may have changed on Google's side
   */
  const checkRenewalStatus = useCallback(async (): Promise<void> => {
    if (!currentUser || !subscriptionDetails) return;
    
    // Only check for active subscriptions nearing their end date
    if (!subscriptionDetails.isNearingEnd) {
      return;
    }
    
    try {
      secureLogger.log('Checking subscription renewal status for subscription nearing end date');
      
      const functions = getFunctions();
      
      // Different handling based on platform
      if (subscriptionDetails.platform === 'android' && 
          subscriptionDetails.googlePlayReceipt?.purchaseToken) {
        
        // For Android, check with specific Google renewal API
        const checkRenewalFunc = httpsCallable(functions, 'checkGooglePlayAutoRenewalStatus');
        const result = await checkRenewalFunc({
          purchaseToken: subscriptionDetails.googlePlayReceipt.purchaseToken
        });
        
        const data = result.data as any;
        secureLogger.log('Renewal status check result:', data);
        
        // If status wasn't updated on server, update locally
        if (!data.statusUpdated && data.autoRenewing !== undefined) {
          // Check if renewal status has changed
          const currentAutoRenewing = subscriptionDetails.googlePlayReceipt.autoRenewing;
          if (currentAutoRenewing !== data.autoRenewing) {
            secureLogger.log(`Auto-renewal status changed from ${currentAutoRenewing} to ${data.autoRenewing}`);
            
            // Update local state
            setSubscriptionDetails({
              ...subscriptionDetails,
              googlePlayReceipt: {
                ...subscriptionDetails.googlePlayReceipt,
                autoRenewing: data.autoRenewing
              }
            });
          }
        }
      } 
      else if ((subscriptionDetails.platform === 'ios' || subscriptionDetails.platform === 'macos') && 
               subscriptionDetails.appleReceipt) {
        // For iOS, we would check Apple's status, but that requires a server call
        // This would be implemented similarly to the Android version
        secureLogger.log('Apple renewal status checking not implemented yet');
      }
    } catch (error) {
      secureLogger.error('Error checking renewal status:', error);
    }
  }, [currentUser, subscriptionDetails]);

  // Effect to check renewal status for subscriptions nearing end
  useEffect(() => {
    if (!subscriptionDetails || !subscriptionDetails.isNearingEnd) {
      return;
    }
    
    // Check immediately on load if we're nearing end
    checkRenewalStatus();
    
    // Also check periodically while viewing
    const intervalId = setInterval(() => {
      checkRenewalStatus();
    }, 3600000); // Once per hour
    
    return () => clearInterval(intervalId);
  }, [subscriptionDetails?.isNearingEnd, checkRenewalStatus]);

  // Clear localStorage subscription data on login/logout
  useEffect(() => {
    // When user changes (login or logout), clear cached subscription data
    localStorage.removeItem(SUBSCRIPTION_DETAILS_KEY);
    localStorage.removeItem(LAST_VERIFICATION_KEY);
    localStorage.removeItem(INITIAL_VERIFICATION_KEY);
    
    // Reset state when user changes
    if (!currentUser) {
      setIsPremium(false);
      setSubscriptionDetails(null);
      setIsLoading(false);
      setError(null);
    }
  }, [currentUser?.uid]); // Only trigger when user ID changes (login/logout)

  // Update subscription details and store in localStorage
  const updateSubscriptionDetails = useCallback((details: SubscriptionDetails | null, isPremiumValue: boolean) => {
    setSubscriptionDetails(details);
    setIsPremium(isPremiumValue);
    
    // Store in localStorage for access during timeout checks
    if (currentUser) {
      try {
        localStorage.setItem(SUBSCRIPTION_DETAILS_KEY, JSON.stringify({
          isPremium: isPremiumValue,
          subscription: details,
          lastUpdated: Date.now()
        }));
      } catch (error) {
        secureLogger.error('Error storing subscription details in localStorage:', error);
      }
    }
  }, [currentUser]);

  // Improved date conversion with better error handling
  const safelyConvertToDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    
    try {
      if (dateValue instanceof Date) {
        // Verify it's a valid date by checking if it's not NaN
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }
      
      if (typeof dateValue === 'string') {
        const parsedDate = new Date(dateValue);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
      }
      
      if (typeof dateValue === 'number') {
        const parsedDate = new Date(dateValue);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
      }
      
      secureLogger.error('Unsupported date format:', dateValue);
      return null;
    } catch (error) {
      secureLogger.error('Error converting date:', error, dateValue);
      return null;
    }
  };

  // Process subscription data from the database with improved date handling
  const processSubscriptionData = useCallback((subData: any): SubscriptionDetails => {
    const now = Date.now();
    
    // Convert date strings to Date objects using the safer conversion function
    const periodEnd = safelyConvertToDate(subData.periodEnd);
    const periodStart = safelyConvertToDate(subData.periodStart);
    
    // Check if subscription has expired - safely handle date comparison
    let isExpired = false;
    try {
      isExpired = periodEnd !== null && periodEnd < new Date();
    } catch (error) {
      secureLogger.error('Error checking if subscription expired:', error);
      isExpired = false;
    }
    
    // Determine if the subscription is active
    let isActive = false;
    
    if (isExpired) {
      isActive = false;
    } else if (subData.status === 'active') {
      isActive = true;
    } else if ((subData.status === 'canceled' || subData.status === 'inactive') && 
               periodEnd !== null && periodEnd > new Date(now)) {
      isActive = true;
    }
    
    // Calculate if subscription is nearing its end - safely handle date operations
    let isNearingEnd = false;
    try {
      isNearingEnd = periodEnd !== null && 
                   (periodEnd.getTime() - now < NEARING_END_THRESHOLD_MS);
    } catch (error) {
      secureLogger.error('Error calculating if subscription is nearing end:', error);
      isNearingEnd = false;
    }
    
    // Calculate next verification time
    const nextVerificationTime = getNextVerificationTime({
      ...subData,
      periodEnd: periodEnd instanceof Date ? periodEnd : null,
      status: subData.status as any
    });
    
    // Calculate next renewal date if the function is available
    let nextRenewalDate: Date | null | undefined = undefined;
    if (typeof calculateNextRenewalDate === 'function') {
      nextRenewalDate = calculateNextRenewalDate({
        ...subData,
        periodEnd: periodEnd instanceof Date ? periodEnd : null,
        periodStart: periodStart instanceof Date ? periodStart : null,
        platform: (subData.platform as 'web' | 'ios' | 'android' | 'macos' | 'unknown') || 'unknown',
        status: subData.status as any
      });
    }
    
    // Update local state based on subscription status
    const status = subData.status as 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trial' | 'expired' | 'inactive';
    const isActiveStatus = (status === 'active' || 
                         ((status === 'canceled' || status === 'inactive') && !isExpired) || 
                         status === 'trial');
    
    // Create the processed subscription details object
    const processedData = {
      status: subData.status as 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trial' | 'expired' | 'paused' | 'refunded' | 'inactive',
      periodEnd: periodEnd,
      periodStart: periodStart,
      cancelAtPeriodEnd: subData.cancelAtPeriodEnd === true,
      platform: (subData.platform as 'web' | 'ios' | 'android' | 'macos' | 'unknown') || 'web',
      planId: subData.planId,
      lastVerified: safelyConvertToDate(subData.lastVerified) || new Date(),
      nextVerificationTime: subData.nextVerificationTime || getNextVerificationTime(subData),
      appleReceipt: subData.appleReceipt,
      googlePlayReceipt: subData.googlePlayReceipt,
      webReceipt: subData.webReceipt,
      lastVerificationResult: subData.lastVerificationResult || 'pending',
      verificationMessages: subData.verificationMessages || [],
      displayStatus: subData.displayStatus || getDisplayStatus(status, isExpired, isNearingEnd),
      isCanceledButActive: status === 'canceled' && !isExpired,
      isNearingEnd: isNearingEnd,
      canCreateTest: isActiveStatus,
      autoRenewing: getAutoRenewingStatus(subData),
      nextRenewalDate: nextRenewalDate
    };
    
    // Use the updateSubscriptionDetails function
    updateSubscriptionDetails(processedData, isActiveStatus);
    
    return processedData;
  }, [updateSubscriptionDetails, getNextVerificationTime, getAutoRenewingStatus]);

  // Check subscription status from the database
  const checkSubscription = useCallback(async (forceCheck: boolean = false): Promise<boolean> => {
    if (!currentUser) {
      secureLogger.log('No current user, cannot check subscription');
      return false;
    }
    
    // Prevent multiple simultaneous verification attempts
    if (isVerifyingRef.current && !forceCheck) {
      secureLogger.log('Verification already in progress, skipping duplicate check');
      return isPremium;
    }
    
    // Set verification in progress flag
    isVerifyingRef.current = true;
    
    setIsLoading(true);
    secureLogger.log('Checking subscription status for user', currentUser.uid);
    
    try {
      // First, check if we have a subscription in the database
      const db = getDatabase();
      const userRef = ref(db, `users/${currentUser.uid}`);
      const snapshot = await get(userRef);
      
      if (!snapshot.exists()) {
        secureLogger.log('User data not found in database');
        setIsLoading(false);
        isVerifyingRef.current = false;
        return false;
      }
      
      const userData = snapshot.val();
      secureLogger.log('User data retrieved from database');
      
      // Update isPremium state based on database value
      const isPremiumValue = userData.isPremium === true;
      
      // If user has subscription data, process it
      if (userData.subscription) {
        const subscription = userData.subscription;
        const now = Date.now();
        
        secureLogger.log(`Processing subscription data with status: ${subscription.status}`);
        
        // Check if subscription has a periodEnd and it's in the future
        let isActive = false;
        let canCreateTest = false;
        
        // Safely convert periodEnd to a Date object for comparison
        let periodEnd: Date | null = null;
        try {
          if (subscription.periodEnd) {
            if (subscription.periodEnd instanceof Date) {
              periodEnd = subscription.periodEnd;
            } else if (typeof subscription.periodEnd === 'string') {
              periodEnd = new Date(subscription.periodEnd);
            } else if (typeof subscription.periodEnd === 'number') {
              periodEnd = new Date(subscription.periodEnd);
            }
          }
        } catch (error) {
          secureLogger.error('Error converting periodEnd in checkSubscription:', error);
          periodEnd = null;
        }
        
        // Handle different subscription states
        if (subscription.status === 'active' && periodEnd && periodEnd.getTime() > now) {
          // Case 1: isPremium=true and not expired and active
          secureLogger.log('Subscription is active and not expired');
          isActive = true;
          canCreateTest = true;
          
          // Ensure database has the correct values
          await update(userRef, {
            isPremium: true,
            'subscription/canCreateTest': true
          });
        }
        else if ((subscription.status === 'canceled' || subscription.status === 'inactive') && 
                 periodEnd && periodEnd.getTime() > now) {
          // Case 2: isPremium=true and not expired and inactive/cancelled subscription
          secureLogger.log('Subscription is canceled/inactive but not expired, allowing test creation');
          isActive = true;
          canCreateTest = true;
          
          // Ensure database has the correct values
          await update(userRef, {
            isPremium: true,
            'subscription/canCreateTest': true,
            'subscription/isCanceledButActive': true,
            'subscription/displayStatus': 'Canceled (Active Until Expiration)'
          });
        }
        else if ((subscription.status === 'expired' || 
                 (periodEnd && periodEnd.getTime() < now)) && 
                 subscription.status !== 'paused') {
          // Case 3: isPremium=false and expired and inactive
          secureLogger.log('Subscription is expired');
          isActive = false;
          canCreateTest = false;
          
          // Ensure database has the correct values
          await update(userRef, {
            isPremium: false,
            'subscription/canCreateTest': false,
            'subscription/status': 'expired',
            'subscription/displayStatus': 'Expired'
          });
        }
        else if (subscription.status === 'paused' && periodEnd && periodEnd.getTime() > now) {
          // Case 5: Paused but not expired
          secureLogger.log('Subscription is paused but not expired');
          isActive = false;
          canCreateTest = false;
          
          // Ensure database has the correct values
          await update(userRef, {
            isPremium: false,
            'subscription/canCreateTest': false,
            'subscription/displayStatus': 'On Hold'
          });
        }
        else if (subscription.status === 'paused' && periodEnd && periodEnd.getTime() < now) {
          // Case 6: Paused and expired
          secureLogger.log('Subscription is paused and expired');
          isActive = false;
          canCreateTest = false;
          
          // Ensure database has the correct values
          await update(userRef, {
            isPremium: false,
            'subscription/canCreateTest': false,
            'subscription/status': 'expired',
            'subscription/displayStatus': 'Expired (Paused)'
          });
        }
        else if (subscription.status === 'refunded') {
          // Case 7: Refunded
          secureLogger.log('Subscription has been refunded');
          isActive = false;
          canCreateTest = false;
          
          // Ensure database has the correct values
          await update(userRef, {
            isPremium: false,
            'subscription/canCreateTest': false,
            'subscription/displayStatus': 'Refunded'
          });
        }
        else if (!subscription.status || !periodEnd) {
          // Case 4: Free user (no valid subscription data)
          secureLogger.log('User has incomplete subscription data, treating as free user');
          isActive = false;
          canCreateTest = false;
        }
        
        // Process the subscription data
        const processedData = processSubscriptionData(subscription);
        
        // Mark initial verification as done
        if (!initialVerificationDone) {
          localStorage.setItem(INITIAL_VERIFICATION_KEY, 'true');
          setInitialVerificationDone(true);
        }
        
        // Store verification time
        lastVerifiedRef.current = Date.now();
        localStorage.setItem(LAST_VERIFICATION_KEY, lastVerifiedRef.current.toString());
        
        secureLogger.log(`Subscription verification complete. Status: ${processedData.status}, Can create test: ${processedData.canCreateTest}`);
        
        setIsLoading(false);
        isVerifyingRef.current = false;
        
        // Return whether the subscription is active
        return processedData.canCreateTest === true;
      }
      
      // No subscription data - free user (Case 4)
      secureLogger.log('No subscription data found, user is on free plan');
      updateSubscriptionDetails(null, isPremiumValue);
      
      // Show a more helpful message for free users with no subscription path
      setError('Please subscribe using the Amsler Grid Daily app on your iOS or Android device to access premium features. Your subscription will automatically sync across all your devices using the same account.');
      
      // Mark initial verification as done
      if (!initialVerificationDone) {
        localStorage.setItem(INITIAL_VERIFICATION_KEY, 'true');
        setInitialVerificationDone(true);
      }
      
      // Store verification time
      lastVerifiedRef.current = Date.now();
      localStorage.setItem(LAST_VERIFICATION_KEY, lastVerifiedRef.current.toString());
      
      setIsLoading(false);
      isVerifyingRef.current = false;
      return isPremiumValue;
    } catch (err) {
      secureLogger.error('Error checking subscription:', err);
      setIsLoading(false);
      isVerifyingRef.current = false;
      return false;
    }
  }, [currentUser, processSubscriptionData, updateSubscriptionDetails, isPremium, initialVerificationDone]);

  // Update the calculateNextRenewalDate function to handle Android differently
  const calculateNextRenewalDate = useCallback((subscription: SubscriptionDetails): Date | null => {
    if (!subscription.periodEnd) return null;

    try {
    const now = new Date();
      
      // Ensure periodEnd is a Date object
      let endDate: Date;
      if (subscription.periodEnd instanceof Date) {
        endDate = subscription.periodEnd;
      } else if (typeof subscription.periodEnd === 'string') {
        endDate = new Date(subscription.periodEnd);
      } else if (typeof subscription.periodEnd === 'number') {
        endDate = new Date(subscription.periodEnd);
      } else {
        secureLogger.error('Invalid periodEnd format in calculateNextRenewalDate:', subscription.periodEnd);
        return null;
      }

    // If subscription is already expired, return null
    if (endDate < now) return null;

    // Special handling for Android subscriptions
    if (subscription.platform === 'android' && subscription.googlePlayReceipt) {
      // For Android, we only show next renewal if autoRenewing is true
      if (subscription.googlePlayReceipt.autoRenewing === true) {
        // Get the subscription period in milliseconds
        let periodLength = 0;
          
        if (subscription.periodStart && subscription.periodEnd) {
            // Ensure periodStart is a Date object
            let startDate: Date;
            if (subscription.periodStart instanceof Date) {
              startDate = subscription.periodStart;
            } else if (typeof subscription.periodStart === 'string') {
              startDate = new Date(subscription.periodStart);
            } else if (typeof subscription.periodStart === 'number') {
              startDate = new Date(subscription.periodStart);
            } else {
              // Default to monthly if we can't determine the period
              return new Date(endDate.getTime() + (30 * 24 * 3600000));
            }
            
            periodLength = endDate.getTime() - startDate.getTime();
                } else {
          // Default to monthly if we can't determine the period
          periodLength = 30 * 24 * 3600000; // 30 days
        }
          
        return new Date(endDate.getTime() + periodLength);
      }
      // If not auto-renewing, just return the end date
      return endDate;
    }

    // For other platforms, calculate next renewal date
    if (subscription.autoRenewing) {
      let periodLength = 0;
        
      if (subscription.periodStart && subscription.periodEnd) {
          // Ensure periodStart is a Date object
          let startDate: Date;
          if (subscription.periodStart instanceof Date) {
            startDate = subscription.periodStart;
          } else if (typeof subscription.periodStart === 'string') {
            startDate = new Date(subscription.periodStart);
          } else if (typeof subscription.periodStart === 'number') {
            startDate = new Date(subscription.periodStart);
          } else {
            // Default to monthly if we can't determine the period
            return new Date(endDate.getTime() + (30 * 24 * 3600000));
          }
          
          periodLength = endDate.getTime() - startDate.getTime();
            } else {
        periodLength = 30 * 24 * 3600000; // 30 days
      }
        
      return new Date(endDate.getTime() + periodLength);
    }

    return endDate;
    } catch (error) {
      secureLogger.error('Error in calculateNextRenewalDate:', error);
      return null;
    }
  }, []);

  // Additional effect to check subscription only on login
  useEffect(() => {
    if (!currentUser || initialVerificationDone) return;
    
    secureLogger.log("Performing initial subscription verification on login");
    
    // Set a flag to prevent multiple verification attempts
    const doInitialVerification = async () => {
      try {
        await checkSubscription(true);
        localStorage.setItem(INITIAL_VERIFICATION_KEY, 'true');
        setInitialVerificationDone(true);
      } catch (error) {
        secureLogger.error("Error during initial verification:", error);
      }
    };
    
    doInitialVerification();
    
  }, [currentUser, checkSubscription, initialVerificationDone]);

  // Add back minimal Firebase listeners to keep subscription data in sync
  useEffect(() => {
    if (!currentUser) return;
    
    secureLogger.log("Setting up minimal subscription listeners for user:", currentUser.uid);
    
    const db = getDatabase();
    
    // Listen to the root isPremium flag to ensure we catch all status changes
    const userIsPremiumRef = ref(db, `users/${currentUser.uid}/isPremium`);
    const userSubscriptionRef = ref(db, `users/${currentUser.uid}/subscription`);
    
    // Track all listeners to properly clean them up
    const listeners: Array<() => void> = [];
    
    // Listen to the root isPremium flag
    const isPremiumUnsubscribe = onValue(userIsPremiumRef, (snapshot) => {
      secureLogger.log("isPremium update received:", snapshot.val());
      // Just update the local state without triggering verification
      const isPremiumValue = snapshot.val();
      setIsPremium(isPremiumValue === true);
    });
    listeners.push(isPremiumUnsubscribe);
    
    // Listen to subscription changes but don't trigger verification
    const subscriptionUnsubscribe = onValue(userSubscriptionRef, (snapshot) => {
      secureLogger.log("Subscription data updated");
      const subData = snapshot.val();
      
      if (subData) {
        // Process the subscription data without triggering verification
        processSubscriptionData(subData);
        
        // Store in localStorage for quick access
        try {
          localStorage.setItem(SUBSCRIPTION_DETAILS_KEY, JSON.stringify({
            isPremium: isPremium,
            subscription: subData,
            lastUpdated: Date.now()
          }));
        } catch (error) {
          secureLogger.error('Error storing subscription details in localStorage:', error);
        }
      } else {
        // No subscription data
        setSubscriptionDetails(null);
      }
    });
    listeners.push(subscriptionUnsubscribe);
    
    // Return a cleanup function that unsubscribes from all listeners
    return () => {
      secureLogger.log("Cleaning up subscription listeners");
      listeners.forEach(unsubscribe => unsubscribe());
    };
  }, [currentUser, processSubscriptionData, isPremium]);

  // Add this effect to ensure loading state doesn't get stuck
  useEffect(() => {
    if (isLoading) {
      // If loading state has been active for more than 30 seconds, clear it
      const timeoutId = setTimeout(() => {
        secureLogger.log("Loading timeout triggered - clearing loading state");
        setIsLoading(false);
        isVerifyingRef.current = false;
        
        // Set a helpful error message for users
        setError('Please subscribe using the Amsler Grid Daily app on your iOS or Android device to access premium features. Your subscription will automatically sync across all your devices using the same account.');
        
        // If we don't have subscription details yet, set minimal default values
        if (!subscriptionDetails) {
          setSubscriptionDetails({
            status: 'canceled' as 'canceled', // Use a valid status type
            periodEnd: null,
            periodStart: null,
            cancelAtPeriodEnd: false,
            platform: 'web',
            lastVerified: new Date(),
            lastVerificationResult: 'failed',
            verificationMessages: ['Verification timed out - Please subscribe using the mobile app'],
            nextVerificationTime: Date.now() + VERIFICATION_COOLDOWN_MS,
            canCreateTest: false
          });
        }
        
        // Check if we have cached data in localStorage that we can use
        try {
          const storedData = localStorage.getItem(SUBSCRIPTION_DETAILS_KEY);
          if (storedData) {
            const subData = JSON.parse(storedData);
            const now = Date.now();
            const lastUpdated = subData.lastUpdated || 0;
            
            // Only use cached data if it's less than 1 hour old
            if (now - lastUpdated < 60 * 60 * 1000) {
              secureLogger.log('Using cached subscription data from localStorage after timeout');
              
              // If user has an active subscription that hasn't expired, use it
              if (subData.isPremium === true && 
                  subData.subscription?.periodEnd && 
                  new Date(subData.subscription.periodEnd).getTime() > now) {
                
                setIsPremium(true);
                setSubscriptionDetails(subData.subscription);
              }
            }
          }
        } catch (error) {
          secureLogger.error('Error reading subscription data from localStorage:', error);
        }
      }, 30000); // 30 seconds timeout
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, subscriptionDetails]);

  // Function to upgrade to premium - modified to show mobile app subscription message
  const upgradeToPremuim = async () => {
    if (!currentUser) {
      setError('You must be logged in to upgrade');
      throw new Error('User not logged in');
    }

    setIsLoading(true);
    setError(null);

    try {
      // For web users, we'll show a message about subscribing via mobile app
      // This complies with Apple and Google payment policies
      setError('Please subscribe using the Amsler Grid Daily app on your iOS or Android device. Your subscription will automatically sync across all your devices using the same account.');
      
      // We're not actually creating a subscription here, just showing the message
      // In the future, this could be replaced with a Stripe integration
      return;
    } catch (err) {
      secureLogger.error('Error upgrading to premium:', err);
      setError('Failed to process upgrade. Please try again later.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to cancel subscription
  const cancelSubscription = async (): Promise<boolean> => {
    if (!currentUser) {
      setError('You must be logged in to cancel');
      throw new Error('User not logged in');
    }

    if (!subscriptionDetails) {
      setError('No active subscription found');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const platform = subscriptionDetails.platform;
      
      switch (platform) {
        case 'android':
          if (subscriptionDetails.googlePlayReceipt?.purchaseToken) {
            const cancelAndroidSubscription = httpsCallable(functions, 'cancelGooglePlaySubscription');
            const result = await cancelAndroidSubscription({
              purchaseToken: subscriptionDetails.googlePlayReceipt.purchaseToken
            });
            
            secureLogger.log('Android subscription cancellation result:', result);
            
            // Update local state immediately for better UX
            setSubscriptionDetails({
              ...subscriptionDetails,
              cancelAtPeriodEnd: true,
              lastVerificationResult: 'success',
              lastVerified: new Date(),
              verificationMessages: [
                ...(subscriptionDetails.verificationMessages || []),
                `Subscription canceled on ${new Date().toISOString()}`
              ]
            });
            
            // Trigger a verification to update all state
            setTimeout(() => checkSubscription(), 1000);
            
            return true;
          } else {
            setError('Missing purchase token for cancellation');
            return false;
          }
          
        case 'ios':
        case 'macos':
          setError('To cancel your subscription, please open the Apple App Store > Account > Subscriptions and cancel from there. Changes will sync automatically.');
          return false;
          
        case 'web':
          setError('Web subscription cancellation not implemented');
          return false;
          
        default:
          setError(`Unknown platform: ${platform}`);
          return false;
      }
    } catch (err) {
      secureLogger.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription. Please try again later.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Update the subscription status display to handle Android differently
  const getSubscriptionStatusDisplay = useCallback(() => {
    if (!subscriptionDetails) return null;
    
    // Determine status based on various conditions
    let statusText = 'Active';
    let statusClass = 'subscription-active';
    let showCancellationInfo = false;
    let showRenewalInfo = false;
    let showExpiredInfo = false;
    
    // Enhanced status display with improved Android cancellation detection
    if (subscriptionDetails.status === 'expired') {
      statusText = 'Expired';
      statusClass = 'subscription-expired';
      showExpiredInfo = true;
    } else if (subscriptionDetails.status === 'canceled') {
      if (subscriptionDetails.periodEnd && subscriptionDetails.periodEnd > new Date()) {
        statusText = 'Canceled (Active until expiration)';
        statusClass = 'subscription-canceled-active';
        showCancellationInfo = true;
      } else {
        statusText = 'Canceled';
        statusClass = 'subscription-canceled';
      }
    } else if (subscriptionDetails.status === 'inactive') {
      if (subscriptionDetails.periodEnd && subscriptionDetails.periodEnd > new Date()) {
        statusText = 'Inactive (Active until expiration)';
        statusClass = 'subscription-canceled-active';
        showCancellationInfo = true;
      } else {
        statusText = 'Inactive';
        statusClass = 'subscription-canceled';
      }
    } else if (subscriptionDetails.platform === 'android' && 
        (subscriptionDetails.googlePlayReceipt?.autoRenewing === false ||
         subscriptionDetails.isCanceledButActive === true)) {
      statusText = 'Canceled (Active until expiration)';
      statusClass = 'subscription-canceled-active';
      showCancellationInfo = true;
    } else if (subscriptionDetails.cancelAtPeriodEnd) {
      statusText = 'Canceled (Active until expiration)';
      statusClass = 'subscription-canceled-active';
      showCancellationInfo = true;
    } else if (subscriptionDetails.isNearingEnd) {
      // Check auto-renewal status for subscriptions nearing end
      const willAutoRenew = 
        (subscriptionDetails.platform === 'android' && subscriptionDetails.googlePlayReceipt?.autoRenewing === true) ||
        ((['ios', 'macos'].includes(subscriptionDetails.platform)) && subscriptionDetails.appleReceipt?.autoRenewStatus === true) ||
        (subscriptionDetails.platform === 'web' && !subscriptionDetails.cancelAtPeriodEnd);
      
      if (willAutoRenew) {
        statusText = 'Active (Will Auto-Renew)';
        statusClass = 'subscription-active-renewing';
        showRenewalInfo = true;
      } else {
        statusText = 'Expiring Soon';
        statusClass = 'subscription-expiring-soon';
        showRenewalInfo = true;
      }
    }
    
    // If we have displayStatus, use it as the authoritative value
    if (subscriptionDetails.displayStatus) {
      statusText = subscriptionDetails.displayStatus;
    }
    
    // Format expiration date and next renewal date if available
    let dateText = '';
    if (subscriptionDetails.periodEnd) {
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      
      // For Android subscriptions, show different text based on auto-renewal status
      if (subscriptionDetails.platform === 'android') {
        const isAutoRenewing = subscriptionDetails.googlePlayReceipt?.autoRenewing === true;
        dateText = `Valid until: ${subscriptionDetails.periodEnd.toLocaleDateString(undefined, options)}`;
        
        if (isAutoRenewing) {
          dateText += `\nWill auto-renew on expiration date`;
        } else {
          dateText += `\nAuto-renewal is turned off`;
        }
      } else {
        // For other platforms, show standard date information
        dateText = `Valid until: ${subscriptionDetails.periodEnd.toLocaleDateString(undefined, options)}`;
        
        if (subscriptionDetails.autoRenewing && subscriptionDetails.nextRenewalDate) {
          dateText += `\nNext renewal: ${subscriptionDetails.nextRenewalDate.toLocaleDateString(undefined, options)}`;
        }
      }
    }
    
    return (
      <div className={`subscription-status ${statusClass}`}>
        <div className="status-text">Status: {statusText}</div>
        {dateText && <div className="expiration-date">{dateText}</div>}
        {subscriptionDetails.platform && <div className="platform-info">Platform: {subscriptionDetails.platform}</div>}
        {showCancellationInfo && (
          <div className="cancellation-info">
            {subscriptionDetails.platform === 'android' 
              ? 'Your subscription will remain active until the expiration date shown above.'
              : 'Your subscription has been canceled but remains active until the end date shown above.'}
          </div>
        )}
        {showRenewalInfo && (
          <div className="renewal-info">
            {subscriptionDetails.platform === 'android'
              ? (subscriptionDetails.googlePlayReceipt?.autoRenewing 
                ? 'Your subscription will automatically renew on the expiration date shown above.'
                : 'Your subscription will expire on the date shown above unless you turn on auto-renewal.')
              : (statusClass === 'subscription-active-renewing' 
                ? 'Your subscription will automatically renew on the expiration date shown above.'
                : 'Your subscription will expire on the date shown above unless you renew.')}
          </div>
        )}
        {showExpiredInfo && (
          <div className="expired-info">
            Your subscription has expired. Please renew to continue using premium features.
          </div>
        )}
        {!subscriptionDetails.cancelAtPeriodEnd && 
         subscriptionDetails.status !== 'canceled' && 
         subscriptionDetails.status !== 'expired' &&
         subscriptionDetails.googlePlayReceipt?.autoRenewing !== false && 
         !subscriptionDetails.isCanceledButActive && (
          <button 
            onClick={cancelSubscription}
            className="cancel-subscription-button">
            Cancel Subscription
          </button>
        )}
      </div>
    );
  }, [subscriptionDetails, cancelSubscription]);

  // Check storage usage
  const checkStorageUsage = useCallback(async () => {
    if (!currentUser) return 0;
    try {
      const userRef = ref(getDatabase(), `users/${currentUser.uid}/testResults`);
      const snapshot = await get(userRef);
      if (!snapshot.exists()) return 0;
      
      // Calculate approximate storage size in MB
      const data = snapshot.val();
      const storageSize = new Blob([JSON.stringify(data)]).size / (1024 * 1024);
      setStorageUsage(storageSize);
      return storageSize;
    } catch (error) {
      secureLogger.error('Error checking storage usage:', error);
      return 0;
    }
  }, [currentUser]);

  // Check active devices
  const checkActiveDevices = useCallback(async () => {
    if (!currentUser) return 0;
    try {
      const devicesRef = ref(getDatabase(), `users/${currentUser.uid}/devices`);
      const snapshot = await get(devicesRef);
      if (!snapshot.exists()) return 0;
      
      const devices = snapshot.val();
      const activeCount = Object.values(devices).filter((device: any) => 
        device.lastActive && Date.now() - device.lastActive < 30 * 24 * 60 * 60 * 1000
      ).length;
      
      setActiveDevices(activeCount);
      return activeCount;
    } catch (error) {
      secureLogger.error('Error checking active devices:', error);
      return 0;
    }
  }, [currentUser]);

  // Check if user has exceeded free limits
  const checkFreeLimits = useCallback(async () => {
    if (isPremium) return { hasExceeded: false };
    
    const storage = await checkStorageUsage();
    const devices = await checkActiveDevices();
    
    // Get test count
    let testCount = 0;
    try {
      const userRef = ref(getDatabase(), `users/${currentUser?.uid}/testResults`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        testCount = Object.keys(data).length;
      }
    } catch (error) {
      secureLogger.error('Error checking test count:', error);
    }
    
    return {
      hasExceeded: storage > FREE_USER_LIMITS.MAX_STORAGE_MB || 
                  devices > FREE_USER_LIMITS.MAX_DEVICES ||
                  testCount >= FREE_USER_LIMITS.MAX_TESTS,
      exceededLimits: {
        storage: storage > FREE_USER_LIMITS.MAX_STORAGE_MB,
        devices: devices > FREE_USER_LIMITS.MAX_DEVICES,
        tests: testCount >= FREE_USER_LIMITS.MAX_TESTS
      }
    };
  }, [isPremium, checkStorageUsage, checkActiveDevices, currentUser]);

  // Update device activity
  useEffect(() => {
    if (!currentUser) return;
    
    const updateDeviceActivity = async () => {
      const deviceId = localStorage.getItem('deviceId') || crypto.randomUUID();
      localStorage.setItem('deviceId', deviceId);
      
      const deviceRef = ref(getDatabase(), `users/${currentUser.uid}/devices/${deviceId}`);
      await update(deviceRef, {
        lastActive: Date.now(),
        platform: 'web',
        userAgent: navigator.userAgent
      });
    };
    
    updateDeviceActivity();
    const interval = setInterval(updateDeviceActivity, 30 * 60 * 1000); // Update every 30 minutes
    
    return () => clearInterval(interval);
  }, [currentUser]);

  // Regular checks for free user limits
  useEffect(() => {
    if (!currentUser || isPremium) return;
    
    const checkLimits = async () => {
      const { hasExceeded, exceededLimits } = await checkFreeLimits();
      if (hasExceeded) {
        setError(
          `You have exceeded your free plan limits. ${
            exceededLimits?.storage ? 'Storage usage exceeds 100MB. ' : ''
          }${
            exceededLimits?.devices ? 'Too many active devices. ' : ''
          }${
            exceededLimits?.tests ? 'You have reached the limit of 10 tests. ' : ''
          }Please upgrade to premium to continue using all features.`
        );
      }
    };
    
    checkLimits();
    const interval = setInterval(checkLimits, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(interval);
  }, [currentUser, isPremium, checkFreeLimits]);

  /**
   * Verify subscription status before saving test results
   * This function checks if the user has an active premium subscription
   * before allowing them to save test results
   * 
   * @returns Promise<boolean> - true if user can save test, false otherwise
   */
  const verifyBeforeSavingTest = async (): Promise<boolean> => {
    if (!currentUser) {
      setError('You must be logged in to save test results');
      return false;
    }
    
    // DEBUG: Disable all verification
    if (DEBUG_DISABLE_ALL_VERIFICATION) {
      secureLogger.log('DEBUG: All verification disabled - skipping verifyBeforeSavingTest');
      if (DEBUG_ALLOW_PREMIUM_REGARDLESS) {
        return true;
      }
      return isPremium;
    }
    
    // If we already know the user is premium and have subscription details, do a quick check
    if (isPremium && !isLoading && subscriptionDetails) {
      // Double check that the subscription hasn't expired
      const now = Date.now();
      
      // Case 1: Active subscription that's not expired
      if (subscriptionDetails.status === 'active' && 
          subscriptionDetails.periodEnd instanceof Date && 
          subscriptionDetails.periodEnd.getTime() > now) {
        secureLogger.log('User has active subscription that is not expired - allowing test');
      return true;
    }
    
      // Case 2: Canceled or inactive subscription that's not expired yet
      if ((subscriptionDetails.status === 'canceled' || subscriptionDetails.status === 'inactive') && 
          subscriptionDetails.periodEnd instanceof Date && 
          subscriptionDetails.periodEnd.getTime() > now) {
        secureLogger.log('User has canceled/inactive subscription that is not expired yet - allowing test');
      return true;
    }
    
      // Case 3: Expired subscription - check if it's recently expired
      if (subscriptionDetails.status === 'expired' || 
          (subscriptionDetails.periodEnd instanceof Date && 
           subscriptionDetails.periodEnd.getTime() <= now)) {
        secureLogger.log('User subscription is expired - checking if recent');
        
        // If expired within the last 24 hours, do a full verification
        // This helps catch cases where the subscription was renewed on another device
        if (subscriptionDetails.periodEnd instanceof Date && 
            now - subscriptionDetails.periodEnd.getTime() < 24 * 60 * 60 * 1000) {
          secureLogger.log('Subscription expired recently - performing full verification');
          return await checkSubscription(true);
        }
        
        secureLogger.log('Subscription expired more than 24 hours ago - denying test');
          return false;
        }
        
      // Case 5: Paused subscription
      if (subscriptionDetails.status === 'paused') {
        secureLogger.log('User subscription is paused - denying test');
      return false;
    }
    
      // Case 7: Refunded subscription
      if (subscriptionDetails.status === 'refunded') {
        secureLogger.log('User subscription has been refunded - denying test');
            return false;
      }
    }
    
    // If we're here, we need to do a full verification
    secureLogger.log('Performing full subscription verification before saving test');
    return await checkSubscription(true);
  };

  const value = {
    isPremium,
    isLoading,
    error,
    subscriptionDetails,
    storageUsage,
    activeDevices,
    freeLimits: FREE_USER_LIMITS,
    checkFreeLimits,
    upgradeToPremuim,
    checkSubscription,
    cancelSubscription,
    getSubscriptionStatusDisplay,
    verifyBeforeSavingTest
  };

  // DEV ONLY: Add testing helpers for development and debugging
  if (process.env.NODE_ENV === 'development') {
    // Add a function to simulate Android cancellation
    (value as any)._devSimulateAndroidCancellation = async () => {
      if (!currentUser || !subscriptionDetails) {
        secureLogger.error("No user or subscription to simulate cancellation with");
        return false;
      }
      
      if (subscriptionDetails.platform !== 'android') {
        secureLogger.error("Can only simulate Android cancellations");
        return false;
      }
      
      secureLogger.log("Simulating Android subscription cancellation");
      
      try {
        // 1. Add testing flag to simulate cancellation
        const db = getDatabase();
        const testConfigRef = ref(db, `testConfig/androidSubscriptions/${currentUser.uid}`);
        await set(testConfigRef, {
          simulateCanceled: true,
          timestamp: Date.now()
        });
        
        // 2. Force an immediate check
        await checkGooglePlayCancellationStatus({ force: true });
        
        // 3. Run a full subscription check
        await checkSubscription();
        
        return true;
      } catch (error) {
        secureLogger.error("Failed to simulate Android cancellation:", error);
        return false;
      }
    };
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider; 